(() => {
  if (window.aboutOptInteractions) {
    window.aboutOptInteractions.init(document);
    return;
  }

  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealObservers = new WeakMap();
  const bioControllers = new WeakMap();
  const timelineControllers = new WeakMap();
  const testimonialControllers = new WeakMap();
  const revealGroups = [
    ['.about-opt__hero-copy', 0],
    ['.about-opt__why > h2', 0],
    ['.about-opt__why-copy', 100],
    ['.about-opt__photo-grid', 0],
    ['.about-opt__practitioner-copy', 120],
    ['.about-opt__story-card header', 0],
    ['.about-opt__roadmap > header', 0],
    ['.about-opt__target', 80],
    ['.about-opt__roadmap .about-opt__number-list', 160],
    ['.about-opt__education-art', 0],
    ['.about-opt__education-copy > header', 0],
    ['.about-opt__education .about-opt__number-list li', 0, 100],
    ['.about-opt__clinical > header', 0],
    ['.about-opt__people article', 0, 80],
    ['.about-opt__stats', 80],
    ['.about-opt__testimonials', 120],
    ['.about-opt__future > div', 0],
  ];

  const reveal = (element) => {
    revealObservers.get(element)?.disconnect();
    revealObservers.delete(element);
    element.classList.add('is-revealed');
  };

  const prepareReveal = (element, delay) => {
    if (element.dataset.aboutRevealReady === 'true') return;
    element.dataset.aboutRevealReady = 'true';
    element.dataset.aboutReveal = '';
    element.style.setProperty('--about-reveal-delay', `${delay}ms`);

    if (motionPreference.matches || typeof IntersectionObserver === 'undefined') {
      element.classList.add('is-revealed');
      return;
    }

    element.classList.add('is-reveal-pending');
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) reveal(element);
      },
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' },
    );
    revealObservers.set(element, observer);
    requestAnimationFrame(() => observer.observe(element));
  };

  const initReveals = (scope) => {
    revealGroups.forEach(([selector, delay, stagger = 0]) => {
      scope.querySelectorAll(selector).forEach((element, index) => {
        prepareReveal(element, delay + index * stagger);
      });
    });
  };

  const initPlaceholderMotion = (scope) => {
    if (motionPreference.matches) return;

    scope
      .querySelectorAll('.about-opt__hero--placeholder, .about-opt__future:not([style])')
      .forEach((element) => {
        if (element.dataset.aboutPlaceholderReady === 'true') return;
        element.dataset.aboutPlaceholderReady = 'true';
        element.classList.add('is-placeholder-animated');
        element.addEventListener(
          'animationend',
          (event) => {
            if (event.animationName === 'about-opt-wire-sweep') {
              element.classList.remove('is-placeholder-animated');
            }
          },
          { once: true },
        );
      });
  };

  const initTimeline = (root) => {
    if (timelineControllers.has(root)) return;

    const items = Array.from(root.querySelectorAll('[data-about-timeline-item]'));
    if (items.length === 0) return;

    const abortController = new AbortController();
    const { signal } = abortController;
    let active = items.findIndex((item) => item.classList.contains('is-active'));
    let locked = false;
    if (active < 0) active = 0;
    const defaultActive = active;

    const activate = (index, interacting = true) => {
      active = Math.max(0, Math.min(index, items.length - 1));
      Array.from(root.classList)
        .filter((className) => className.startsWith('is-active-'))
        .forEach((className) => root.classList.remove(className));
      root.classList.add(`is-active-${active + 1}`);
      root.classList.toggle('is-interacting', interacting);

      items.forEach((item, itemIndex) => {
        const isActive = itemIndex === active;
        const hasDetail = item.hasAttribute('data-has-detail');
        item.classList.toggle('is-active', isActive);
        item.classList.toggle('is-past', itemIndex < active);
        item.classList.toggle('is-future', itemIndex > active);
        item.setAttribute('aria-expanded', String(isActive && hasDetail));
        item.setAttribute('aria-pressed', String(isActive));
      });
    };

    const reset = () => activate(defaultActive, false);
    const focusItem = (index) => items[(index + items.length) % items.length].focus();

    items.forEach((item, index) => {
      item.addEventListener('mouseenter', () => activate(index), { signal });
      item.addEventListener('focus', () => activate(index), { signal });
      item.addEventListener(
        'click',
        () => {
          locked = true;
          item.focus();
          activate(index);
        },
        { signal },
      );
      item.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            focusItem(index + 1);
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            focusItem(index - 1);
          } else if (event.key === 'Home') {
            event.preventDefault();
            focusItem(0);
          } else if (event.key === 'End') {
            event.preventDefault();
            focusItem(items.length - 1);
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            locked = true;
            activate(index);
          } else if (event.key === 'Escape') {
            locked = false;
            reset();
            item.blur();
          }
        },
        { signal },
      );
    });

    root.addEventListener(
      'mouseleave',
      () => {
        if (!locked && !root.contains(document.activeElement)) reset();
      },
      { signal },
    );
    root.addEventListener(
      'focusout',
      () => {
        window.setTimeout(() => {
          if (!locked && !root.contains(document.activeElement)) reset();
        });
      },
      { signal },
    );
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (root.contains(event.target)) return;
        locked = false;
        reset();
      },
      { signal },
    );

    const destroy = () => {
      abortController.abort();
      timelineControllers.delete(root);
    };

    timelineControllers.set(root, { activate, destroy, reset });
    reset();
  };

  const initBios = (root) => {
    if (bioControllers.has(root)) return;

    const toggles = Array.from(root.querySelectorAll('[data-about-bio-toggle]'));
    if (toggles.length === 0) return;

    const abortController = new AbortController();
    const { signal } = abortController;

    const setOpen = (openIndex) => {
      toggles.forEach((toggle, index) => {
        const isOpen = index === openIndex;
        const card = toggle.closest('article');
        const panel = card.querySelector('[data-about-bio-panel]');
        card.classList.toggle('is-bio-open', isOpen);
        toggle.setAttribute('aria-expanded', String(isOpen));
        panel.setAttribute('aria-hidden', String(!isOpen));
      });
    };

    toggles.forEach((toggle, index) => {
      toggle.addEventListener(
        'click',
        () => setOpen(toggle.getAttribute('aria-expanded') === 'true' ? -1 : index),
        { signal },
      );
      toggle.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Escape') return;
          setOpen(-1);
          toggle.focus();
        },
        { signal },
      );
    });

    const destroy = () => {
      abortController.abort();
      bioControllers.delete(root);
    };

    bioControllers.set(root, { destroy, setOpen, toggles });
    setOpen(-1);
  };

  const initTestimonials = (root) => {
    if (testimonialControllers.has(root)) return;

    const cards = Array.from(root.querySelectorAll('.about-opt__testimonial'));
    if (cards.length === 0) return;

    const dots = Array.from(root.querySelectorAll('[data-testimonial-index]'));
    const abortController = new AbortController();
    const { signal } = abortController;
    let active = cards.findIndex((card) => card.classList.contains('is-active'));
    let autoplayTimer;
    if (active < 0) active = 0;

    const pauseAutoplay = () => {
      window.clearInterval(autoplayTimer);
      autoplayTimer = undefined;
    };

    const canAutoplay = () =>
      cards.length > 1 &&
      !motionPreference.matches &&
      !document.hidden &&
      !root.matches(':hover') &&
      !root.contains(document.activeElement);

    const startAutoplay = () => {
      pauseAutoplay();
      if (!canAutoplay()) return;
      autoplayTimer = window.setInterval(() => show(active + 1), 5000);
    };

    const show = (index, restart = false) => {
      active = (index + cards.length) % cards.length;
      const previous = (active - 1 + cards.length) % cards.length;
      const next = (active + 1) % cards.length;

      cards.forEach((card, cardIndex) => {
        const isActive = cardIndex === active;
        const isPrevious = cards.length > 2 && cardIndex === previous;
        const isNext = cards.length > 1 && cardIndex === next;
        const isSelectable = isPrevious || isNext;
        card.classList.toggle('is-active', isActive);
        card.classList.toggle('is-prev', isPrevious);
        card.classList.toggle('is-next', isNext);
        card.setAttribute('aria-hidden', String(!(isActive || isSelectable)));
        if (isActive || isSelectable) {
          card.setAttribute('role', 'button');
          card.setAttribute('tabindex', isActive ? '0' : '-1');
          card.setAttribute('aria-label', `Show testimonial ${cardIndex + 1}`);
        } else {
          card.removeAttribute('role');
          card.removeAttribute('aria-label');
          card.setAttribute('tabindex', '-1');
        }
      });
      dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === active;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (restart) startAutoplay();
    };

    root
      .querySelector('[data-testimonial-prev]')
      ?.addEventListener('click', () => show(active - 1, true), { signal });
    root
      .querySelector('[data-testimonial-next]')
      ?.addEventListener('click', () => show(active + 1, true), { signal });
    dots.forEach((dot) => {
      dot.addEventListener('click', () => show(Number(dot.dataset.testimonialIndex), true), { signal });
    });
    cards.forEach((card, index) => {
      card.addEventListener(
        'click',
        () => {
          if (index !== active) show(index, true);
        },
        { signal },
      );
    });
    root.addEventListener('mouseenter', pauseAutoplay, { signal });
    root.addEventListener('mouseleave', startAutoplay, { signal });
    root.addEventListener('focusin', pauseAutoplay, { signal });
    root.addEventListener('focusout', () => window.setTimeout(startAutoplay), { signal });
    document.addEventListener('visibilitychange', startAutoplay, { signal });
    motionPreference.addEventListener('change', startAutoplay, { signal });

    const destroy = () => {
      pauseAutoplay();
      abortController.abort();
      testimonialControllers.delete(root);
    };

    testimonialControllers.set(root, { destroy, show });
    show(active);
    startAutoplay();
  };

  const quoteScrollers = new WeakMap();

  const initQuoteScroller = (root) => {
    const scroller = root.querySelector('.about-opt__quotes');
    if (!scroller || quoteScrollers.has(scroller)) return;

    const abortController = new AbortController();
    const { signal } = abortController;
    const desktop = window.matchMedia('(min-width: 990px)');
    let dragged = false;
    let pointerId = null;
    let startX = 0;
    let startScroll = 0;
    let userMoved = false;

    const alignToDesign = () => {
      if (!desktop.matches || userMoved) return;
      scroller.scrollLeft = 240;
    };

    scroller.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      event.preventDefault();
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = scroller.scrollLeft;
      dragged = false;
      scroller.setPointerCapture(event.pointerId);
    }, { signal });

    scroller.addEventListener('dragstart', (event) => {
      event.preventDefault();
    }, { capture: true, signal });

    scroller.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 4) {
        dragged = true;
        userMoved = true;
        scroller.classList.add('is-dragging');
      }
      scroller.scrollLeft = startScroll - delta;
    }, { signal });

    const endDrag = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      scroller.classList.remove('is-dragging');
    };

    scroller.addEventListener('pointerup', endDrag, { signal });
    scroller.addEventListener('pointercancel', endDrag, { signal });
    scroller.addEventListener('click', (event) => {
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
      dragged = false;
    }, { capture: true, signal });

    desktop.addEventListener('change', alignToDesign, { signal });
    alignToDesign();

    quoteScrollers.set(scroller, () => {
      abortController.abort();
      quoteScrollers.delete(scroller);
    });
  };

  const initPhases = (root) => {
    const cards = Array.from(root.querySelectorAll('[data-phase]'));
    if (cards.length === 0 || root.dataset.aboutPhasesReady === 'true') return;
    root.dataset.aboutPhasesReady = 'true';

    const images = Array.from(root.querySelectorAll('[data-funnel-image]'));

    const showFunnel = (index) => {
      if (images.length === 0) return;
      const key = index === null ? 'default' : String(index);
      images.forEach((image) => {
        const active = image.dataset.funnelImage === key;
        image.classList.toggle('is-active', active);
        image.hidden = !active;
      });
    };

    const select = (card) => {
      const turningOff = card.classList.contains('is-active');
      cards.forEach((item) => {
        const active = !turningOff && item === card;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      showFunnel(turningOff ? null : card.dataset.funnelIndex);
    };

    cards.forEach((card) => {
      card.addEventListener('click', () => select(card));
    });
  };

  const initFaq = (root) => {
    const items = Array.from(root.querySelectorAll('[data-faq-item]'));
    if (items.length === 0 || root.dataset.aboutFaqReady === 'true') return;
    root.dataset.aboutFaqReady = 'true';

    items.forEach((item) => {
      const button = item.querySelector('[data-faq-toggle]');
      const icon = item.querySelector('.about-opt__faq-icon');
      const toggle = () => {
        const open = item.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(open));
        if (icon) icon.textContent = open ? '×' : '+';
      };
      button?.addEventListener('click', (event) => {
        if (event.target.closest('a')) return;
        toggle();
      });
      button?.addEventListener('keydown', (event) => {
        if (event.target.closest('a')) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        toggle();
      });
    });
  };

  const init = (scope) => {
    initPlaceholderMotion(scope);
    initReveals(scope);
    scope.querySelectorAll('.about-opt__people').forEach(initBios);
    scope.querySelectorAll('[data-about-timeline]').forEach(initTimeline);
    scope.querySelectorAll('[data-testimonials]').forEach(initTestimonials);
    scope.querySelectorAll('.about-opt__testimonials').forEach(initQuoteScroller);
    scope.querySelectorAll('[data-about-phases]').forEach(initPhases);
    scope.querySelectorAll('[data-about-faq]').forEach(initFaq);
  };

  const destroy = (scope) => {
    scope.querySelectorAll('[data-about-reveal]').forEach((element) => {
      revealObservers.get(element)?.disconnect();
      revealObservers.delete(element);
    });
    scope.querySelectorAll('[data-testimonials]').forEach((root) => {
      testimonialControllers.get(root)?.destroy();
    });
    scope.querySelectorAll('.about-opt__quotes').forEach((scroller) => {
      quoteScrollers.get(scroller)?.();
    });
    scope.querySelectorAll('[data-about-timeline]').forEach((root) => {
      timelineControllers.get(root)?.destroy();
    });
    scope.querySelectorAll('.about-opt__people').forEach((root) => {
      bioControllers.get(root)?.destroy();
    });
  };

  window.aboutOptInteractions = { init, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target.closest('.about-opt__testimonial');
    if (card) {
      const root = card.closest('[data-testimonials]');
      testimonialControllers.get(root)?.show(
        Array.from(root.querySelectorAll('.about-opt__testimonial')).indexOf(card),
        true,
      );
    }

    const timelineItem = event.target.closest('[data-about-timeline-item]');
    if (timelineItem) {
      const root = timelineItem.closest('[data-about-timeline]');
      timelineControllers
        .get(root)
        ?.activate(Array.from(root.querySelectorAll('[data-about-timeline-item]')).indexOf(timelineItem));
    }

    const bioCard = event.target.closest('.about-opt__people article');
    if (bioCard) {
      const root = bioCard.closest('.about-opt__people');
      const controller = bioControllers.get(root);
      const toggle = bioCard.querySelector('[data-about-bio-toggle]');
      if (toggle) controller?.setOpen(controller.toggles.indexOf(toggle));
    }
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const timelineItem = event.target.closest('[data-about-timeline-item]');
    if (timelineItem) {
      timelineControllers.get(timelineItem.closest('[data-about-timeline]'))?.reset();
    }

    const bioRoot = event.target.closest('.about-opt__people');
    if (bioRoot) bioControllers.get(bioRoot)?.setOpen(-1);
  });
})();
